import axios from 'axios';
import { useEffect, useSyncExternalStore } from 'react';

// EXPERT ASSEMBLY RULES (Louis 2026-11-02): what a craftsman knows and a manual's drawings do not say
// — cross tightening, screws left loose until the frame is square. One list for every manual, kept
// on the server (see notice_rules.py), read and edited in Phase 4's third subphase (ExpertRules), and
// the ones switched on handed to every sub-step call (see rulesBlock, in stepAnalyzePrompt).
export type ExpertRule = { id: number; title: string; body: string; enabled: boolean; position: number };

let rules: ExpertRule[] = [];
let loaded = false;
let loading: Promise<void> | null = null;
const listeners = new Set<() => void>();
const set = (next: ExpertRule[]) => { rules = next; loaded = true; listeners.forEach((l) => l()); };

// THE LIST, fetched once and kept: the run waits for it before writing its first prompt.
export function loadExpertRules(again = false): Promise<void> {
  if (loading && !again) return loading;
  loading = axios.get<{ rules: ExpertRule[] }>('/api/notice/expert-rules')
    .then(({ data }) => set(data.rules))
    .catch(() => { loading = null; });
  return loading;
}

export function useExpertRules(): { rules: ExpertRule[]; loaded: boolean } {
  useEffect(() => { void loadExpertRules(); }, []);
  const now = useSyncExternalStore((l) => {
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, () => rules);
  return { rules: now, loaded };
}

export async function addExpertRule(title: string, body: string) {
  const { data } = await axios.post<{ rule: ExpertRule }>('/api/notice/expert-rules', { title, body });
  set([...rules, data.rule]);
}

export async function updateExpertRule(id: number, change: Partial<Pick<ExpertRule, 'title' | 'body' | 'enabled'>>) {
  const { data } = await axios.put<{ rule: ExpertRule }>(`/api/notice/expert-rules/${id}`, change);
  set(rules.map((r) => (r.id === id ? data.rule : r)));
}

export async function deleteExpertRule(id: number) {
  await axios.delete(`/api/notice/expert-rules/${id}`);
  set(rules.filter((r) => r.id !== id));
}

// THE RULES SWITCHED ON, as the sub-step prompt says them; '' where none is.
export function rulesBlock(): string {
  const on = rules.filter((r) => r.enabled);
  if (!on.length) return '';
  return 'EXPERT ASSEMBLY RULES. A craftsman follows these whatever the manual draws; the sub-steps ' +
    '(their order, how many there are, and what each one says) must respect every one that applies ' +
    'to this step, and ignore the ones that do not:\n' +
    on.map((r) => `- ${r.title}: ${r.body}`).join('\n');
}
